import { useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, ArrowRight } from "lucide-react";
import { mediaUrl } from "../lib/api";

export const Lightbox = ({ images, index, onClose, onNavigate }) => {
  const open = index !== null && index >= 0;
  const draggingRef = useRef(false);

  const go = useCallback(
    (dir) => {
      if (!open) return;
      const next = (index + dir + images.length) % images.length;
      onNavigate(next);
    },
    [open, index, images, onNavigate]
  );

  useEffect(() => {
    if (!open) return;
    const lenis = typeof window !== "undefined" ? window.__lenis : null;
    lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      lenis?.start();
    };
  }, [open, go, onClose]);

  if (typeof document === "undefined") return null;
  const img = open ? images[index] : null;

  const handleDragEnd = (_e, info) => {
    const { offset, velocity } = info;
    if (offset.x < -80 || velocity.x < -500) go(1);
    else if (offset.x > 80 || velocity.x > 500) go(-1);
    // keep the drag flag briefly so the trailing click doesn't close
    setTimeout(() => { draggingRef.current = false; }, 60);
  };

  const handleImageClick = () => {
    if (draggingRef.current) return;
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      {open && img && (
        <motion.div
          data-testid="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[10000] bg-[var(--vsl-bg)]/85 backdrop-blur-sm flex items-center justify-center overflow-hidden"
          onClick={onClose}
        >
          {/* extra darkening layer beneath the image */}
          <div data-testid="lightbox-scrim" className="absolute inset-0 bg-black/60 pointer-events-none" />

          <button
            data-testid="lightbox-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-6 right-6 md:top-8 md:right-10 text-[var(--vsl-on-media)] hover:text-[var(--vsl-limestone)] transition-colors z-20"
          >
            <X size={28} strokeWidth={1.3} />
          </button>

          <span data-testid="lightbox-counter" className="absolute top-7 md:top-9 left-1/2 -translate-x-1/2 font-mono text-[11px] tracking-[0.3em] text-white z-20">
            {String(index + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
          </span>

          <button
            data-testid="lightbox-prev"
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous"
            className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-[var(--vsl-on-media)]/70 hover:text-[var(--vsl-on-media)] transition-colors z-20 p-3"
          >
            <ArrowLeft size={26} strokeWidth={1.3} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next"
            className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-[var(--vsl-on-media)]/70 hover:text-[var(--vsl-on-media)] transition-colors z-20 p-3"
          >
            <ArrowRight size={26} strokeWidth={1.3} />
          </button>

          <AnimatePresence mode="wait">
            {img.type === "video" ? (
              <motion.video
                key={img.id}
                data-testid="lightbox-video"
                src={mediaUrl(img.url)}
                poster={img.poster ? mediaUrl(img.poster) : undefined}
                controls
                autoPlay
                playsInline
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 max-w-[90vw] max-h-[86vh] md:max-h-[80vh] object-contain shadow-2xl select-none vsl-shimmer"
              />
            ) : (
              <motion.img
                key={img.id}
                data-testid="lightbox-image"
                src={mediaUrl(img.url)}
                alt=""
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.5}
                onDragStart={() => { draggingRef.current = true; }}
                onDragEnd={handleDragEnd}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                onClick={(e) => { e.stopPropagation(); handleImageClick(); }}
                className="relative z-10 max-w-[90vw] max-h-[86vh] md:max-h-[80vh] object-contain shadow-2xl cursor-pointer touch-none select-none"
              />
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
